import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Dropdown, message } from 'antd';
import { DownOutlined, ScanOutlined } from '@ant-design/icons';
import { onError } from 'utils/handlers';
import leerDocumento from 'api/lecturaDeDocumentos';

const TAMANO_MAXIMO_MB = 5;
const TIPOS_PERMITIDOS = ['application/pdf', 'image/jpeg', 'image/png'];

const NOMBRE_DEL_CAMPO = {
  curp: 'CURP',
  rfc: 'RFC',
  nss: 'Número de Seguridad Social',
  cic: 'Número de Identificación',
};

const normalizarNombre = (texto) => (texto || '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .replace(/\s+/g, ' ')
  .trim()
  .toUpperCase();

// Pasa los datos leídos a los campos del formulario de Filiación. Solo se llenan los que se
// pudieron leer, y se avisa si el nombre del documento no coincide con el del registro.
// La CURP se toma de la INE frontal; si ya está capturada o leída, la constancia fiscal y la del
// IMSS no la vuelven a escribir (aunque se lea la CURP, no se sobrescribe la que ya hay).
// El código postal fiscal lo manda la constancia fiscal; la INE solo lo llena si está vacío.
// Al leer el reverso de la INE se elige también el tipo de identificación "INE" del catálogo
// (`extras.tiposDeIdentificaciones`).
export const aplicarDatosDeDocumento = (tipo, datos, formFiliacion, form, extras = {}) => {
  const valores = {};
  const curpActual = formFiliacion.getFieldValue('curp');
  if (datos.curp && (tipo === 'INE' || !curpActual)) valores.curp = datos.curp;
  if (tipo === 'CSF' && datos.rfc) valores.RFC = datos.rfc;
  if (tipo === 'NSS' && datos.nss) valores.numero_de_seguridad_social = datos.nss;
  if (tipo === 'INE_REVERSO' && datos.cic) {
    valores.numero_de_identificacion = datos.cic;
    const tipos = extras.tiposDeIdentificaciones || [];
    const tipoIne = tipos.find((item) => normalizarNombre(item?.nombre) === 'INE')
      || tipos.find((item) => /\bINE\b|CREDENCIAL PARA VOTAR/.test(normalizarNombre(item?.nombre)));
    if (tipoIne) valores.tipo_de_identificacion = tipoIne.id;
  }
  const cpActual = formFiliacion.getFieldValue('codigo_postal_fiscal_texto_libre');
  if (datos.codigo_postal && (tipo === 'CSF' || (tipo === 'INE' && !cpActual))) {
    valores.codigo_postal_fiscal_texto_libre = datos.codigo_postal;
    // Si el CP cambia se limpian estado y municipio para que Filiación los vuelva a consultar.
    if (datos.codigo_postal !== cpActual) {
      valores.estado = undefined;
      valores.municipio = undefined;
    }
  }

  const llenados = Object.keys(valores).filter((campo) => valores[campo] !== undefined);
  if (!llenados.length) {
    if (datos.curp && curpActual) {
      message.info('La CURP ya estaba capturada; no se modificó.');
    } else {
      message.warning('No se pudo leer ningún dato del documento.');
    }
    return;
  }
  formFiliacion.setFieldsValue(valores);
  formFiliacion.validateFields(llenados).catch(() => {});

  const etiquetas = [];
  if (valores.curp) etiquetas.push(NOMBRE_DEL_CAMPO.curp);
  if (valores.RFC) etiquetas.push(NOMBRE_DEL_CAMPO.rfc);
  if (valores.numero_de_seguridad_social) etiquetas.push(NOMBRE_DEL_CAMPO.nss);
  if (valores.numero_de_identificacion) etiquetas.push(NOMBRE_DEL_CAMPO.cic);
  if (valores.tipo_de_identificacion) etiquetas.push('Tipo de Identificación');
  if (valores.codigo_postal_fiscal_texto_libre) etiquetas.push('Código Postal Fiscal');
  message.success(`Se llenó: ${etiquetas.join(', ')}. Verifique que sea correcto.`);

  const leido = normalizarNombre(
    [datos.nombre, datos.apellido_paterno, datos.apellido_materno].filter(Boolean).join(' '),
  );
  const capturado = normalizarNombre([
    form?.getFieldValue('nombre'),
    form?.getFieldValue('apellido_paterno'),
    form?.getFieldValue('apellido_materno'),
  ].filter(Boolean).join(' '));
  if (leido && capturado && leido !== capturado) {
    message.warning(`El nombre del documento (${leido}) no coincide con el capturado.`, 6);
  }
};

const esMovil = () => /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)
  && navigator.maxTouchPoints > 0;

// Botón para leer un documento (constancia fiscal, INE frontal, INE reverso o constancia del
// IMSS) y llenar los campos de Filiación con lo que se lea de él. Dos formas de entregarlo:
//  - Subir Archivo: PDF, JPG o PNG.
//  - Leer con la Cámara del Teléfono (solo celulares): abre la cámara y la foto completa se sube
//    como imagen; el OCR busca en ella el dato.
function LecturaDeDocumento({
  tipo, texto, disabled, onDatos,
}) {
  const [loading, setLoading] = useState(false);
  const archivoRef = useRef(null);
  const camaraRef = useRef(null);

  const enviar = async (archivo) => {
    if (!TIPOS_PERMITIDOS.includes(archivo.type)) {
      message.warning('Solo se permiten archivos PDF, JPG o PNG.');
      return;
    }
    if (archivo.size / 1024 / 1024 > TAMANO_MAXIMO_MB) {
      message.warning(`El archivo no debe pesar más de ${TAMANO_MAXIMO_MB} MB.`);
      return;
    }
    try {
      setLoading(true);
      const datos = await leerDocumento(tipo, archivo);
      onDatos(datos);
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const alElegir = (e) => {
    const [archivo] = e.target.files;
    e.target.value = '';
    if (archivo) enviar(archivo);
  };

  const abrirCamara = () => {
    if (!esMovil()) {
      message.info('Esta opción solo está disponible desde un celular. En computadora use "Subir Archivo".', 6);
      return;
    }
    camaraRef.current?.click();
  };

  const items = [
    { key: 'archivo', label: 'Subir Archivo', onClick: () => archivoRef.current?.click() },
    { key: 'camara', label: 'Leer con la Cámara del Teléfono', onClick: abrirCamara },
  ];

  return (
    <>
      <Dropdown menu={{ items }} trigger={['click']} disabled={disabled || loading}>
        <Button icon={<ScanOutlined />} loading={loading} disabled={disabled}>
          {texto}
          <DownOutlined />
        </Button>
      </Dropdown>
      <input
        ref={archivoRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        style={{ display: 'none' }}
        onChange={alElegir}
      />
      <input
        ref={camaraRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={alElegir}
      />
    </>
  );
}

LecturaDeDocumento.propTypes = {
  tipo: PropTypes.oneOf(['CSF', 'INE', 'INE_REVERSO', 'NSS', 'DOMICILIO']).isRequired,
  texto: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  onDatos: PropTypes.func.isRequired,
};

LecturaDeDocumento.defaultProps = {
  disabled: false,
};

export default LecturaDeDocumento;
